import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, MessageSquare, ShoppingCart, UserPlus, CreditCard, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

type Notif = {
  id: string;
  type: string;
  title: string;
  description: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
};

type Msg = {
  id: string;
  name: string;
  message: string;
  created_at: string;
  read_by_admin: boolean;
};

const iconFor = (type: string) => {
  switch (type) {
    case "order":
      return ShoppingCart;
    case "user":
      return UserPlus;
    case "payment":
      return CreditCard;
    default:
      return Bell;
  }
};

const Badge = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold ring-2 ring-background">
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

export const MessageBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Msg[]>([]);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    const { data } = await supabase
      .from("messages")
      .select("id,name,message,created_at,read_by_admin")
      .order("created_at", { ascending: false })
      .limit(8);
    setItems(data ?? []);
    setUnread((data ?? []).filter((m) => !m.read_by_admin).length);
    const { count } = await supabase
      .from("messages")
      .select("id", { count: "exact", head: true })
      .eq("read_by_admin", false);
    if (typeof count === "number") setUnread(count);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("admin-msgs-bell")
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, load)
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const markAllRead = async () => {
    await supabase.from("messages").update({ read_by_admin: true }).eq("read_by_admin", false);
    load();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-10 w-10" aria-label="Messages">
          <MessageSquare className="h-5 w-5" />
          <Badge count={unread} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[92vw] sm:w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-sm">Messages</div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              <Check className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No messages yet</div>
          ) : (
            items.map((m) => (
              <button
                key={m.id}
                onClick={() => navigate("/admin/messages")}
                className={cn(
                  "w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/60 transition-colors flex gap-3",
                  !m.read_by_admin && "bg-primary/5"
                )}
              >
                <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                  <MessageSquare className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{m.name}</span>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{m.message}</p>
                </div>
                {!m.read_by_admin && <span className="h-2 w-2 rounded-full bg-destructive mt-2 shrink-0" />}
              </button>
            ))
          )}
        </div>
        <Link to="/admin/messages" className="block text-center text-sm py-2.5 border-t hover:bg-muted/60 font-medium">
          View all messages
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notif[]>([]);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .neq("type", "message")
      .order("created_at", { ascending: false })
      .limit(10);
    setItems(data ?? []);
    const { count } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("is_read", false)
      .neq("type", "message");
    setUnread(count ?? 0);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("admin-notifs-bell")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, load)
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const markAllRead = async () => {
    await supabase.from("notifications").update({ is_read: true }).eq("is_read", false);
    load();
  };

  const open = async (n: Notif) => {
    if (!n.is_read) {
      await supabase.from("notifications").update({ is_read: true }).eq("id", n.id);
    }
    if (n.link) navigate(n.link);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-10 w-10" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          <Badge count={unread} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[92vw] sm:w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-sm">Notifications</div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              <Check className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">You're all caught up</div>
          ) : (
            items.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <button
                  key={n.id}
                  onClick={() => open(n)}
                  className={cn(
                    "w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/60 transition-colors flex gap-3",
                    !n.is_read && "bg-primary/5"
                  )}
                >
                  <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{n.title}</span>
                      <span className="text-[11px] text-muted-foreground shrink-0">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    {n.description && <p className="text-xs text-muted-foreground truncate">{n.description}</p>}
                  </div>
                  {!n.is_read && <span className="h-2 w-2 rounded-full bg-destructive mt-2 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
